import React from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { LogOut, RefreshCw, AlertTriangle } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';

interface AuthErrorStateProps {
  error?: Error | null;
  onRetry?: () => void;
  onLogout?: () => void;
}

export const AuthErrorState: React.FC<AuthErrorStateProps> = ({ 
  error, 
  onRetry, 
  onLogout 
}) => {
  const { logout } = useAuthStore();

  const handleLogout = () => {
    logout();
    onLogout?.();
  };

  const isAuthError = error?.message?.includes('403') || 
                     error?.message?.includes('401') || 
                     error?.message?.includes('Token') ||
                     error?.message?.includes('Unauthorized');

  if (!error) {
    return null;
  }

  return (
    <div className="p-6">
      <Card>
        <CardContent className="p-6">
          <div className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              {isAuthError ? (
                <LogOut className="w-6 h-6 text-red-600" />
              ) : (
                <AlertTriangle className="w-6 h-6 text-red-600" />
              )}
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 text-foreground mb-2">
              {isAuthError ? 'Problema de Autenticação' : 'Erro ao Carregar Dados'}
            </h3>
            
            <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
              {isAuthError 
                ? 'Sua sessão pode ter expirado. Tente fazer login novamente.'
                : error.message || 'Ocorreu um erro inesperado.'
              }
            </p>

            <div className="flex gap-2 justify-center">
              {onRetry && !isAuthError && (
                <Button onClick={onRetry} variant="outline">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Tentar Novamente
                </Button>
              )}
              
              {isAuthError ? (
                <Button onClick={handleLogout} className="bg-red-600 hover:bg-red-700">
                  <LogOut className="w-4 h-4 mr-2" />
                  Fazer Login Novamente
                </Button>
              ) : (
                <Button onClick={handleLogout} variant="outline">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sair
                </Button>
              )}
            </div>

            {process.env.NODE_ENV === 'development' && (
              <details className="mt-4 text-left">
                <summary className="cursor-pointer text-sm font-medium text-muted-foreground mb-2">
                  Detalhes do erro (dev)
                </summary>
                <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-32">
                  {error.message}
                  {error.stack && '\n\n' + error.stack}
                </pre>
              </details>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
